import { useGlobalState } from '@highlight/ipc/react'
import { UpdateState } from '@shared/types/UpdateTypes'
import { createContext, memo, useContext, useMemo, type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import { UpdateAnnouncement } from './update-announcement'
import { ChangelogAnnouncement } from './changelog-announcement'
import { ReinstallAnnouncement } from './reinstall-announcement'
import { useHasNewChangelog } from '@renderer/windows/features/changelogs/atoms'
import { useLdFlag } from '@renderer/chat/hooks/useLdFlag'
import { isStrandedLegacyMacBuild } from '@shared/utils/builds/distribution.shared'

const LEGACY_MAC_REINSTALL_NAG_FLAG = 'legacy-macos-reinstall-nag'

const AnnouncementClassContext = createContext<string | undefined>(undefined)

const Container = ({ children, className }: React.PropsWithChildren<{ className?: string }>) => {
  const announcementClassName = useContext(AnnouncementClassContext)

  return (
    <div
      className={twMerge(
        // Overlay: a hairline and a barely-lifted tint, matching
        // `SidepanelBanner` so the strips under the top bar read as one system
        // rather than as chrome bolted onto the 2.0 panel.
        'bg-element text-foreground type-prose-sm z-[1] flex w-full flex-col gap-3 border-t p-3',
        // The base window keeps the heavier slab — it has no translucent
        // surface to sit on, and no 2.0 chrome to match.
        'app:border-border app:flex-row app:justify-between app:border-b app:border-t-0 app:text-sm app:sticky app:top-0',
        announcementClassName,
        className,
      )}
    >
      {children}
    </div>
  )
}

const Title = ({ children, className }: React.PropsWithChildren<{ className?: string }>) => {
  return <div className={twMerge('flex items-center gap-2 font-medium', className)}>{children}</div>
}

const Description = ({ children, className }: React.PropsWithChildren<{ className?: string }>) => {
  return <div className={twMerge('text-foreground-secondary leading-normal', className)}>{children}</div>
}

const Actions = ({ children, className }: React.PropsWithChildren<{ className?: string }>) => {
  return (
    <div
      className={twMerge(
        '-webkit-app-region-no-drag flex items-center justify-start gap-2',
        'app:flex-row-reverse',
        className,
      )}
    >
      {children}
    </div>
  )
}

// Fixed for the life of the process — it's baked into the package at build time.
const isStrandedBuild = isStrandedLegacyMacBuild()

interface AnnouncementProps {
  className?: string
}

const Root = memo(({ className }: AnnouncementProps) => {
  const [updateState] = useGlobalState<UpdateState | undefined>('update.state')
  const [updateDismissedAt] = useGlobalState<number | undefined>('update.dismissed')
  const hasNewChangelog = useHasNewChangelog()
  const showReinstallNag = useLdFlag<boolean>(LEGACY_MAC_REINSTALL_NAG_FLAG, false)

  const shouldShowUpdateAnnouncement = useMemo(() => {
    return (
      updateState?.state &&
      updateState.update?.notify !== false &&
      !updateState.silent &&
      updateState.state !== 'checking' &&
      updateState.state !== 'not-available' &&
      (!updateDismissedAt || Date.now() - updateDismissedAt > 1 * 60 * 60 * 1000)
    )
  }, [updateState, updateDismissedAt])

  // Outranks the update and changelog strips: this build can't update at all,
  // so nothing it could announce matters until the user reinstalls.
  let announcement: ReactNode = null
  if (isStrandedBuild && showReinstallNag) {
    announcement = <ReinstallAnnouncement />
  } else if (shouldShowUpdateAnnouncement) {
    announcement = <UpdateAnnouncement />
  } else if (hasNewChangelog) {
    announcement = <ChangelogAnnouncement />
  }

  if (!announcement) {
    return null
  }

  return <AnnouncementClassContext.Provider value={className}>{announcement}</AnnouncementClassContext.Provider>
})
Root.displayName = 'Announcement'

export const Announcement = Object.assign(Root, {
  Container,
  Title,
  Description,
  Actions,
})
